import { AxiosError } from 'axios'

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'

import {
  ProductCategory,
  ProductPrice,
  SalesCategory,
} from '@/interfaces/Interfaces'
import { FixedCost } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

// Product type based on API response
export interface Product {
  id: number
  name: string
  type: string
  salesCode: string
  showForSale: boolean
  stockLimit: boolean
  totalQuantity: string
  barCode: string | null
  qrCode: string | null
  measure: string
  alertLimit: number | null
  isAlertable: boolean
  isUnderAlertLimit: boolean
  isPublicVisible: boolean
  averageCost: string
  productCategoryId: number | null
  salesCategoryId: number | null
  createdAt: string
  supplierId: number | null
  groupName: string | null
  description: string | null
  groupConvertion: number
  enableGroup: boolean | null
  isWholesale: boolean | null
  visibleOnline: boolean
  availableForReservation: boolean
  alwaysAvailableForReservation: boolean
  reservationAvailableFrom: string | null
  reservationAvailableTo: string | null
  duration: string | null
  hasDuration: boolean
  onSale: boolean
  onSaleType: string | null
  onSaleDiscountAmount: number | null
  elaborationSteps: unknown[]
  netCost: string
  productCategory: ProductCategory | null
  salesCategory: SalesCategory | null
  fixedCosts: FixedCost[]
  onSalePrice: number | null
  images: unknown[]
  resources: unknown[]
  prices: ProductPrice[]
  attributes: unknown[]
  supplies: unknown[]
}

export interface ProductsQueryParams {
  all_data?: boolean
  page?: number
  per_page?: number
  search?: string
  productId?: number
}

export interface ProductsResponse {
  items: Product[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Interface for infinite query context
export interface InfiniteQueryContext {
  pageParam: number
}

// Interface for the query key structure
export interface ProductsQueryKey {
  search?: string
  businessId: number
  productId?: number
}

const PRODUCTS_QUERY_KEY = 'products'

/**
 * Fetch products from the API
 * Uses different endpoints based on the query type:
 * - When productId is provided: uses /finder endpoint (returns product directly)
 * - Otherwise: uses main endpoint (returns paginated results)
 */
const fetchProducts = async (
  params?: ProductsQueryParams
): Promise<ProductsResponse> => {
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  // If productId is provided, use the finder endpoint
  if (params?.productId) {
    const response = await APIServices.get('/administration/product/finder', {
      params: { productId: params.productId },
    })

    // The finder endpoint returns the product directly, so we need to wrap it
    // in the expected pagination format for consistency
    const product = response.data
    return {
      items: product ? [product[0]] : [],
      totalItems: product ? 1 : 0,
      currentPage: 1,
      totalPages: 1,
    }
  }

  // Otherwise, use the standard paginated endpoint
  const response = await APIServices.get('/administration/product', {
    params: cleanParams,
  })

  return response.data
}

export const useProductsQuery = ({
  params,
  options,
  businessId,
}: {
  params?: ProductsQueryParams
  options?: Partial<UseQueryOptions<ProductsResponse, AxiosError>>
  businessId: number
}) => {
  const queryResult = useQuery<ProductsResponse, AxiosError>({
    queryKey: getProductsQueryKey({
      businessId,
      search: params?.search ?? '',
      productId: params?.productId ?? undefined,
    }),

    queryFn: () => fetchProducts(params),

    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    ...options,
  })

  return {
    products: queryResult.data?.items || [],
    isLoadingProducts: queryResult.isLoading,
    isErrorProducts: queryResult.isError,
    errorProducts: queryResult.error,
    refetchProducts: queryResult.refetch,
    isFetchingProducts: queryResult.isFetching,
    totalProducts: queryResult.data?.totalItems || 0,
    currentPage: queryResult.data?.currentPage || 1,
    totalPages: queryResult.data?.totalPages || 1,
  }
}

/**
 * Custom hook for infinite scrolling products with search functionality
 * Following TanStack Query's infinite query pattern with optimized caching
 *
 * @param businessId - Business ID for scoping
 * @param params - Query parameters including search
 * @param options - TanStack Query options for customizing query behavior
 * @returns Object containing products, loading states, and utility functions
 */
export const useInfiniteProductsQuery = ({
  businessId,
  params,
  options,
}: {
  businessId: number
  params?: ProductsQueryParams
  options?: Partial<
    UseInfiniteQueryOptions<
      ProductsResponse,
      AxiosError,
      ProductsResponse,
      ProductsResponse,
      [string, ProductsQueryKey],
      number
    >
  >
}) => {
  const queryResult = useInfiniteQuery({
    queryKey: getProductsQueryKey({
      businessId,
      search: params?.search ?? '',
      productId: params?.productId ?? undefined,
    }),
    queryFn: async (queryContext: InfiniteQueryContext) => {
      const result = await fetchProducts({
        ...params,
        page: queryContext.pageParam ?? 1,
        per_page: 20,
      })
      return result
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ProductsResponse) => {
      const nextPage =
        lastPage.currentPage < lastPage.totalPages
          ? lastPage.currentPage + 1
          : undefined

      return nextPage
    },
    refetchOnWindowFocus: false,
    // Keep data fresh for 10 minutes (products don't change frequently)
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Keep inactive data in cache for 1 hour (products are often revisited)
    gcTime: 60 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    retry: 3,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  })

  // Flatten all pages into a single array
  const products =
    (
      queryResult?.data as { pages: ProductsResponse[] } | undefined
    )?.pages?.flatMap((page: ProductsResponse) => page?.items || []) || []

  // Get total from first page (all pages have same total)
  const totalItems =
    (queryResult?.data as { pages: ProductsResponse[] } | undefined)?.pages?.[0]
      ?.totalItems || 0

  const result = {
    products,
    totalItems,
    loadedCount: products?.length || 0,
    hasNextPage: queryResult.hasNextPage,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isFetchingNextPage,
    isError: queryResult.isError,
    error: queryResult.error,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
  }

  return result
}

export const getProductsQueryKey = ({
  businessId,
  search,
  productId,
}: {
  businessId: number
  search?: string
  productId?: number
}): [string, ProductsQueryKey] => [
  PRODUCTS_QUERY_KEY,
  { search, businessId, productId },
]
